import React, { useState, useRef } from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { submitLead, apiErrorMessage } from "../lib/attribution";
import { suggestEmail } from "../lib/emailSuggest";
import { SITE } from "../data/mock";
import { Reveal, Eyebrow } from "./Shared";

const GOALS = ["Get stronger", "Lose body fat", "Build conditioning", "Train for HYROX", "Move better / stay healthy", "Not sure yet"];
const EXPERIENCE = ["Brand new to this", "Trained before, been away", "Currently training"];
const CONTACT_METHODS = ["Text", "Call", "Email"];

const inputCls =
  "w-full bg-[#0D0D0F] border border-white/10 focus:border-[#9D0423] focus:outline-none focus:ring-1 focus:ring-[#9D0423]/40 text-white placeholder:text-neutral-600 px-4 py-3 text-sm transition-colors";
const labelCls = "block font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1.5";

export const LeadForm = ({ formName, programInterest }) => {
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const startedAt = useRef(0);
  const markStart = () => {
    if (!startedAt.current) startedAt.current = Date.now();
  };
  const [f, setF] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    primaryGoal: "",
    experienceLevel: "",
    preferredContactMethod: "Text",
    message: "",
    marketingConsent: false,
    hp: "",
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const emailSuggestion = suggestEmail(f.email);

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setError("");
    if (!f.firstName.trim()) return setError("Please enter your first name.");
    const email = f.email.trim();
    if (!email) return setError("Email is required so a coach can follow up with you.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return setError("Please enter a valid email address.");
    setSending(true);
    try {
      await submitLead({ ...f, formName, programInterest, ts: startedAt.current || Date.now() });
      setDone(true);
    } catch (err) {
      setError(apiErrorMessage(err, `We couldn't submit your request. Please try again or call us at ${SITE.phone}.`));
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="bg-[#121214] border border-white/10 p-8" data-testid="lead-form-success">
        <CheckCircle2 size={36} className="text-[#9D0423]" />
        <h3 className="mt-5 font-heading font-extrabold uppercase text-2xl text-white tracking-tight">
          Thanks! A Boston Iron Grit coach will be in touch shortly.
        </h3>
        <p className="mt-3 text-neutral-400 text-[14px] leading-relaxed">
          We usually reply the same day. Can't wait? Call or text{" "}
          <a href={SITE.phoneHref} className="text-white hover:text-[#E56A79] transition-colors">{SITE.phone}</a>.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate onFocusCapture={markStart} onChangeCapture={markStart} className="bg-[#121214] border border-white/10 p-6 sm:p-8 space-y-4" data-testid="lead-form">
      <input type="text" value={f.hp} onChange={set("hp")} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" name="company" />
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls} htmlFor={`lf-first-${formName}`}>First name *</label>
          <input id={`lf-first-${formName}`} data-testid="lead-first-name" required value={f.firstName} onChange={set("firstName")} className={inputCls} placeholder="First name" />
        </div>
        <div>
          <label className={labelCls} htmlFor={`lf-last-${formName}`}>Last name</label>
          <input id={`lf-last-${formName}`} data-testid="lead-last-name" value={f.lastName} onChange={set("lastName")} className={inputCls} placeholder="Last name" />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className={labelCls} htmlFor={`lf-email-${formName}`}>Email *</label>
          <input id={`lf-email-${formName}`} data-testid="lead-email" type="email" required aria-required="true" aria-describedby={`lf-email-help-${formName}`} value={f.email} onChange={set("email")} className={inputCls} placeholder="you@email.com" />
          {emailSuggestion && (
            <button type="button" data-testid="lead-email-suggestion" onClick={() => setF({ ...f, email: emailSuggestion })} className="mt-1.5 text-[12px] text-amber-400 hover:text-white transition-colors text-left">
              Did you mean <span className="underline">{emailSuggestion}</span>?
            </button>
          )}
        </div>
        <div>
          <label className={labelCls} htmlFor={`lf-phone-${formName}`}>Phone</label>
          <input id={`lf-phone-${formName}`} data-testid="lead-phone" type="tel" value={f.phone} onChange={set("phone")} className={inputCls} placeholder="(617) 555-0123" />
        </div>
      </div>
      <p id={`lf-email-help-${formName}`} className="text-[11px] text-neutral-600 -mt-1">Email is required. Add your phone if you prefer a call or text.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className={labelCls} htmlFor={`lf-goal-${formName}`}>Main goal</label>
          <select id={`lf-goal-${formName}`} data-testid="lead-goal" value={f.primaryGoal} onChange={set("primaryGoal")} className={inputCls}>
            <option value="" className="bg-[#121214]">Choose one…</option>
            {GOALS.map((g) => <option key={g} value={g} className="bg-[#121214]">{g}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls} htmlFor={`lf-exp-${formName}`}>Experience</label>
          <select id={`lf-exp-${formName}`} data-testid="lead-experience" value={f.experienceLevel} onChange={set("experienceLevel")} className={inputCls}>
            <option value="" className="bg-[#121214]">Choose one…</option>
            {EXPERIENCE.map((x) => <option key={x} value={x} className="bg-[#121214]">{x}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className={labelCls} htmlFor={`lf-contact-${formName}`}>How should we reach you?</label>
        <div className="flex gap-2" data-testid="lead-contact-method">
          {CONTACT_METHODS.map((m) => (
            <button
              key={m}
              type="button"
              data-testid={`lead-contact-${m.toLowerCase()}`}
              onClick={() => setF({ ...f, preferredContactMethod: m })}
              className={`px-4 py-2 font-mono text-[11px] uppercase tracking-[0.12em] border transition-colors ${
                f.preferredContactMethod === m ? "bg-[#9D0423] border-[#9D0423] text-white" : "border-white/15 text-neutral-400 hover:border-white/40 hover:text-white"
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className={labelCls} htmlFor={`lf-msg-${formName}`}>Anything else? (optional)</label>
        <textarea id={`lf-msg-${formName}`} data-testid="lead-message" rows={3} value={f.message} onChange={set("message")} className={inputCls} placeholder="Injuries, schedule, questions — anything a coach should know." />
      </div>
      <label className="flex items-start gap-3 cursor-pointer select-none" htmlFor={`lf-consent-${formName}`}>
        <input
          id={`lf-consent-${formName}`}
          type="checkbox"
          checked={f.marketingConsent}
          onChange={(e) => setF({ ...f, marketingConsent: e.target.checked })}
          data-testid="lead-consent"
          className="mt-0.5 h-4 w-4 accent-[#9D0423]"
        />
        <span className="text-[12px] text-neutral-500 leading-relaxed">Also send me occasional training tips and gym updates. No spam.</span>
      </label>
      {error && <p role="alert" className="text-[13px] text-[#E56A79]" data-testid="lead-form-error">{error}</p>}
      <button
        type="submit"
        disabled={sending}
        data-testid="lead-submit"
        className="w-full inline-flex items-center justify-center gap-2 bg-[#9D0423] hover:bg-[#7E031C] disabled:opacity-50 disabled:cursor-not-allowed text-white font-mono font-semibold text-[13px] uppercase tracking-[0.14em] px-6 py-4 transition-colors"
      >
        {sending ? "Sending…" : "Talk to a Coach"} <ArrowRight size={15} />
      </button>
      <p className="text-[11px] text-neutral-600 leading-relaxed">
        By submitting, you agree to be contacted by {SITE.name} about your inquiry. We never share your info.
      </p>
    </form>
  );
};

export const LeadBand = ({ formName, programInterest, title, sub }) => (
  <section className="bg-[#0D0D0F] py-20 border-t border-white/5" data-testid={`lead-band-${formName}`}>
    <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-start">
      <Reveal>
        <Eyebrow>Talk to a Coach</Eyebrow>
        <h2 className="font-heading font-extrabold uppercase text-3xl lg:text-4xl text-white tracking-tight leading-tight">{title}</h2>
        <p className="mt-4 text-neutral-400 text-[15px] leading-relaxed max-w-md">{sub}</p>
        <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-500">
          Prefer to talk now? Call or text <a href={SITE.phoneHref} className="text-white hover:text-[#E56A79] transition-colors">{SITE.phone}</a>
        </p>
      </Reveal>
      <Reveal delay={120}>
        <LeadForm formName={formName} programInterest={programInterest} />
      </Reveal>
    </div>
  </section>
);
